import {DirectUpload} from "@rails/activestorage"

const CONCURRENCY = 3;

const withRetry = async (fn, retries = 3) => {
  let attempts = 0;
  while (attempts < retries) {
    try {
      return await fn();
    } catch (error) {
      if (attempts === retries - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempts) * 1000));
      attempts++;
    }
  }
};

export const UPLOADING = 'uploading'
export const SUCCEEDED = 'succeeded'
export const FAILED = 'failed'
export const BACKEND_PROCESSING = 'BACKEND_PROCESSING'
export const ABORTED = 'aborted'

export class MultipleMediasUploader {
  static queue = [];
  static active = false;

  constructor(project, uploading_user, service, unprocessed, files, onProgress, callback) {
    this.project = project
    this.service = service
    this.uploading_user = uploading_user
    this.files = files
    this.unprocessed = unprocessed
    this.onProgress = onProgress
    this.callback = callback
    this.aborted = false
    this.result = []
    this.pcts = Array(files.length).fill(0)
    this.xhrs = {}
    this.loggedMilestones = new Set()
    this.status = UPLOADING
  }

  state() {
    return {
      project: this.project,
      service: this.service,
      totalProgress: this.totalProgress(),
      uploadedFilesCount: this.uploadedFilesCount(),
      filesCount: this.files.length,
      status: this.status
    }
  }

  static enqueue(uploader) {
    this.queue.push(uploader);
    if (!this.active) {
      this.processQueue();
    }
  }

  static async processQueue() {
    if (this.queue.length === 0) {
      this.active = false;
      return;
    }
    this.active = true;
    const uploader = this.queue.shift();
    await uploader.start();
    this.processQueue();
  }

  totalProgress() {
    return Math.ceil(this.pcts.reduce((res, e) => res + e, 0) / this.pcts.length)
  }

  uploadedFilesCount() {
    return this.pcts.filter(p => p === 100).length
  }

  async start() {
    let wakeLock = null;

    const requestWakeLock = async () => {
      try {
        wakeLock = await navigator.wakeLock?.request('screen');
      } catch (e) {
        // Wake Lock not supported or permission denied — continue without it
      }
    };

    const handleVisibilityChange = async () => {
      if (this.status === UPLOADING && document.visibilityState === 'visible') {
        await requestWakeLock();
      }
    };

    await requestWakeLock();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    try {
      for (let i = 0; i < this.files.length; i += CONCURRENCY) {
        if (this.status !== UPLOADING) return;

        const batch = this.files.slice(i, i + CONCURRENCY);
        const results = await Promise.allSettled(
          batch.map((file, j) =>
            withRetry(() => this.uploadFile(file, i + j), 5)
              .catch(err => Promise.reject({ error: err, file }))
          )
        );

        const failure = results.find(r => r.status === 'rejected');
        if (failure) {
          this.status = FAILED;
          this.onProgress(this);

          const { error, file } = failure.reason;
          fetch("/log_upload_failure", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
            },
            body: JSON.stringify({
              user_id: this.uploading_user.id,
              service_id: this.service.id,
              project_id: this.project.id,
              file_name: file.name,
              error_message: error?.message || "Upload failed",
            }),
          });
          return;
        }
      }

      if (this.result.filter(Boolean).length === this.files.length) {
        this.status = BACKEND_PROCESSING;
        this.onProgress(this);
        this.callback(this.project, this.service, this.unprocessed, this.result.map(m => m.signed_id));
        this.status = SUCCEEDED;
        this.onProgress(this);
      }
    } finally {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      wakeLock?.release();
    }
  }

  async uploadFile(file, index) {
    const delegate = {
      directUploadWillCreateBlobWithXHR: (xhr) => {
        this.xhrs[index] = xhr;
        this.onProgress(this);
      },
      directUploadWillStoreFileWithXHR: (xhr) => {
        this.xhrs[index] = xhr;
        xhr.upload.addEventListener("progress", event => {
          this.pcts[index] = Math.ceil(event.loaded * 100 / event.total);
          this.onProgress(this);

          const overall = this.totalProgress();
          const thresholds = [1, 25, 50, 75];
          thresholds.forEach(t => {
            if (overall >= t && !this.loggedMilestones.has(t)) {
              this.loggedMilestones.add(t);
              fetch("/track_uploads", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
                },
                body: JSON.stringify({
                  user_id: this.uploading_user.id,
                  service_id: this.service.id,
                  project_id: this.project.id,
                  progress: overall
                }),
              });
            }
          });
        });
      }
    };

    const upload = new DirectUpload(file, '/rails/active_storage/direct_uploads', delegate);
    return new Promise((resolve, reject) => {
      upload.create((error, blob) => {
        if (error) {
          reject(error);
        } else {
          this.result[index] = blob;
          this.pcts[index] = 100;
          this.onProgress(this);
          resolve();
        }
      });
    });
  }

  abort() {
    Object.values(this.xhrs).forEach(xhr => xhr.abort());
    this.status = ABORTED;
    return this;
  }
}
